// app/hooks/useAccountBalanceQuery.ts
import { useQuery } from "@tanstack/react-query";
import { gxAccountBalanceTotalApi } from "@/app/apiConfig/services/accountsAbstractApiLayer";
import type { BalanceBreakdown } from "@/app/types/accounts";

const ZERO_BALANCE_POLL_INTERVAL_MS = 5_000;
const MAX_ZERO_BALANCE_RETRIES = 3;

const isZeroBalance = (value: number): boolean => value === 0;

const fetchBalanceData = async (
  currencyCode: string,
): Promise<BalanceBreakdown> => {
  const response = await gxAccountBalanceTotalApi({ currencyCode });

  if (response?.status === 1 && response.data) {
    return response.data;
  }

  throw new Error(
    response?.errorMessage ??
      response?.errorCode ??
      "Failed to fetch balance data",
  );
};

/**
 * Shared account-balance query. Both AlternativeBalanceOverview and
 * CashAvailableCard
 */
export const useAccountBalanceQuery = (
  currencyCode: string,
  pollAttemptRef?: React.MutableRefObject<number>,
) => {
  return useQuery({
    queryKey: ["balance", currencyCode],
    queryFn: () => fetchBalanceData(currencyCode),
    enabled: !!currencyCode,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,

    refetchInterval: (query) => {
      if (!pollAttemptRef) return false;
      if (pollAttemptRef.current >= MAX_ZERO_BALANCE_RETRIES) return false;

      const latestData = query.state.data;

      if (!latestData) {
        pollAttemptRef.current += 1;
        return ZERO_BALANCE_POLL_INTERVAL_MS;
      }

      const totalBalanceIsZero = isZeroBalance(
        latestData.totalbalance?.totalBalance ?? 0,
      );
      const assetsBalanceIsZero = isZeroBalance(
        latestData.totalassetsbalance?.totalBalance ?? 0,
      );
      const liabilitiesBalanceIsZero = isZeroBalance(
        latestData.totalliablitiesbalance?.totalBalance ?? 0,
      );

      if (
        totalBalanceIsZero ||
        assetsBalanceIsZero ||
        liabilitiesBalanceIsZero
      ) {
        pollAttemptRef.current += 1;
        return ZERO_BALANCE_POLL_INTERVAL_MS;
      }

      return false;
    },

    refetchIntervalInBackground: false,
  });
};
